<template>
    <div>
        <button
            type="button"
            @click="open = !open"
            class="border mt-1 block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md mt-1 w-full"
        >
            {{ enemy ? enemy.text : $t('messages.select') }}
        </button>
        <SlideOver :title="$tc('messages.battle_enemies', 2)" :show="open" @close="open = false">
            <div>
                <details class="mt-3 sm:col-span-12" :open="openNewEnemyForm">
                    <summary>
                        <PlusButton @click="openNewEnemyForm = !openNewEnemyForm" />
                    </summary>

                    <div class="p-2 border rounded-lg">
                        <form :action="endpoint" method="post" @submit.prevent="submit">
                            <div class="md:grid md:grid-cols-12 md:gap-6">
                                <div class="sm:col-span-12">
                                    <div class="mt-1">
                                        <FormLabel :value="$t('messages.name')" for="name" />
                                        <FormInput
                                            id="name"
                                            v-model="form.name"
                                            name="name"
                                            type="text"
                                            max="191"
                                            class="mt-1 w-full"
                                        />
                                        <FormError :value="form.errors.name" class="mt-2" />
                                    </div>
                                </div>

                                <div class="sm:col-span-12">
                                    <div class="mt-1">
                                        <FormLabel :value="$t('messages.image')" for="image" />
                                        <input
                                            id="image"
                                            required
                                            @input="form.image = $event.target.files[0]"
                                            name="image"
                                            type="file"
                                            max="191"
                                            accept="image/png"
                                            class="mt-1 w-full"
                                        />
                                        <FormError :value="form.errors.image" class="mt-2" />
                                    </div>
                                </div>
                            </div>

                            <div class="flex justify-end mt-5">
                                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                                    {{ $t('messages.save') }}
                                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                                </AppButton>
                            </div>
                        </form>
                    </div>
                </details>
            </div>

            <div class="mt-5">
                {{ $t('messages.select') }}
            </div>

            <fieldset>
                <legend class="sr-only">
                    {{ $tc('messages.battle_enemies', 2) }}
                </legend>
                <div class="space-y-4">
                    <transition-group
                        class="md:grid md:grid-cols-2 md:gap-6"
                        enter-active-class="transition-opacity ease-linear duration-300"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition-opacity ease-linear duration-300"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                        tag="span"
                    >
                        <!--
                      Checked: "border-transparent", Not Checked: "border-gray-300"
                      Active: "ring-2 ring-indigo-500"
                    -->
                        <template v-for="enemy in enemies" :key="enemy.value">
                            <label
                                :class="[
                                    enemy.value == selected
                                        ? 'border-transparent ring-2 ring-indigo-500'
                                        : 'border-gray-300',
                                ]"
                                class="mt-3 relative block bg-white border rounded-lg shadow-sm px-6 py-4 cursor-pointer sm:justify-between focus:outline-none"
                            >
                                <input
                                    type="radio"
                                    name="enemy"
                                    v-model="selected"
                                    :value="enemy.value"
                                    class="sr-only"
                                    aria-labelledby="server-size-1-label"
                                    aria-describedby="server-size-1-description-0 server-size-1-description-1"
                                />
                                <div>
                                    {{ enemy.text }}
                                </div>
                                <div>
                                    <img
                                        :src="enemy.image_path"
                                        class="h-full"
                                        :alt="$tc('messages.battle_enemies', 1)"
                                    />
                                </div>
                                <!--
                              Active: "border", Not Active: "border-2"
                              Checked: "border-indigo-500", Not Checked: "border-transparent"
                            -->
                                <div
                                    :class="[
                                        enemy.value == selected
                                            ? 'border border-indigo-500'
                                            : 'border-2 border-transparent',
                                    ]"
                                    class="absolute -inset-px rounded-lg border-2 pointer-events-none"
                                    aria-hidden="true"
                                ></div>
                            </label>
                        </template>
                    </transition-group>
                </div>
            </fieldset>
        </SlideOver>
    </div>
</template>

<script>
import SlideOver from '@/components/SlideOver.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormError from '@/components/Forms/FormError.vue';
import AppButton from '@/components/AppButton.vue';
import PlusButton from '@/components/Forms/PlusButton.vue';

export default {
    name: 'BattleEnemiesSelectorPanel',
    components: {
        PlusButton,
        SlideOver,
        FormLabel,
        FormInput,
        FormError,
        AppButton,
    },
    props: {
        modelValue: {
            required: true,
        },
        endpoint: {
            required: true,
            type: String,
        },
        enemies: {
            required: false,
            type: Array,
            default: () => [],
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            open: false,
            openNewEnemyForm: !this.enemies.length,
            selected: this.modelValue,
            form: this.$inertia.form({
                name: '',
                image: null,
            }),
        };
    },
    watch: {
        selected(newVal) {
            this.$emit('update:modelValue', newVal);
        },
    },
    computed: {
        enemy() {
            const enemy = this.enemies.find((e) => e.value == this.selected);

            return enemy ? enemy : null;
        },
    },
    methods: {
        submit() {
            this.form.post(this.endpoint, {
                forceFormData: true,
                onSuccess: () => {
                    this.form.reset('name', 'image');
                    this.openNewEnemyForm = false;
                },
            });
        },
    },
};
</script>

<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
